<template>
  <div class="dashboard-box">
    <h2>Company info:</h2>
    <div class="grid-container">
      <div class="overview-info-item">
        <span class="dashboard-label">Company</span>
        <span class="dashboard-value">{{ startup.name }}</span>
      </div>
      <div v-if="startup.city" class="overview-info-item">
        <span class="dashboard-label">City</span>
        <span class="dashboard-value">{{ startup.city | capitalize }}</span>
      </div>
      <div v-if="startup.country" class="overview-info-item">
        <span class="dashboard-label">Country</span>
        <span class="dashboard-value">{{ startup.country | countryName }}</span>
      </div>
      <div
        v-if="startup.primary_sector || startup.secondary_sector"
        class="overview-info-item"
      >
        <span class="dashboard-label">Sectors</span>
        <span class="dashboard-value">
          <el-tooltip :content="formatSectors()" placement="top">
            <span v-text="formatSectors(50)" />
          </el-tooltip>
        </span>
      </div>
      <div v-if="startup.website" class="overview-info-item">
        <span class="dashboard-label">Website</span>
        <span class="dashboard-value">
          <a :href="websiteLink" target="_new">
            {{ startup.website | trim(20) }}
          </a>
        </span>
      </div>
      <div
        v-if="startup.facebook || startup.twitter || startup.linkedin"
        class="overview-info-item"
      >
        <span class="dashboard-label">Social</span>
        <span class="dashboard-value social-container">
          <a
            v-show="startup.facebook"
            :href="facebookLink"
            class="social"
            target="_new"
          >
            <i class="ion-social-facebook" />
          </a>
          <a
            v-show="startup.twitter"
            :href="twitterLink"
            class="social"
            target="_new"
          >
            <i class="ion-social-twitter" />
          </a>
          <a
            v-show="startup.linkedin"
            :href="linkedinLink"
            class="social"
            target="_new"
          >
            <i class="ion-social-linkedin" />
          </a>
        </span>
      </div>
      <div v-if="startup.founded_at" class="overview-info-item">
        <span class="dashboard-label">Founded</span>
        <span class="dashboard-value" v-text="formatFoundedDate()" />
      </div>
      <div v-if="startup.business_type.length" class="overview-info-item">
        <span class="dashboard-label">Business Type</span>
        <span class="dashboard-value">{{ startup.business_type | join }}</span>
      </div>
      <div v-if="startup.employees_count" class="overview-info-item">
        <span class="dashboard-label">Employees No.</span>
        <span class="dashboard-value">{{ startup.employees_count }}</span>
      </div>
      <div v-if="startup.product_type.length" class="overview-info-item">
        <span class="dashboard-label">Product Type</span>
        <span class="dashboard-value">
          {{ startup.product_type | capitalizeArray | join }}
        </span>
      </div>
      <div v-if="startup.stage" class="overview-info-item">
        <span class="dashboard-label">Company stage</span>
        <span class="dashboard-value">{{ startup.stage | stageLabel }}</span>
      </div>
      <div
        v-if="isNumber(startup.annual_revenue) && startup.annual_revenue > 0"
        class="overview-info-item"
      >
        <span class="dashboard-label">Annual Revenue</span>
        <span class="dashboard-value">
          {{ startup.annual_revenue | numberToMoney }}
        </span>
      </div>
      <div
        v-if="
          isNumber(startup.monthly_recurring_revenue) &&
          startup.monthly_recurring_revenue > 0
        "
        class="overview-info-item"
      >
        <span class="dashboard-label">
          <el-tooltip content="Monthly Recurring Revenue" placement="top">
            <span>MRR</span>
          </el-tooltip>
        </span>
        <span class="dashboard-value">
          {{ startup.monthly_recurring_revenue | numberToMoney }}
        </span>
      </div>
      <div v-if="startup.intellectual_property" class="overview-info-item">
        <span class="dashboard-label">
          <el-tooltip content="Intellectual Property Rights" placement="top">
            <span>IP</span>
          </el-tooltip>
        </span>
        <span class="dashboard-value">
          {{ startup.intellectual_property | intellectualPropertyLabel }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapState } from 'vuex'
import { countryName } from '@/services/countries'
import { sectorLabel } from '@/services/sectors'
import { stageLabel } from '@/services/stages'
import { intellectualPropertyLabel } from '@/services/intellectual-property-rights'
import { dateFilter, numberToMoney, trim } from '@/filters'

export default {
  filters: {
    countryName,
    numberToMoney,
    sectorLabel,
    stageLabel,
    intellectualPropertyLabel,
  },
  props: {
    editable: { type: Boolean, default: false },
  },
  computed: {
    ...mapState({
      startup: (state) => state.startupProfile.startup,
    }),
    ...mapGetters({
      websiteLink: 'startupProfile/websiteLink',
      facebookLink: 'startupProfile/facebookLink',
      twitterLink: 'startupProfile/twitterLink',
      linkedinLink: 'startupProfile/linkedinLink',
    }),
  },
  updated() {
    // setting --row-num dynamically worked only with the 'updated' hook
    this.setRowsNumber()
  },
  methods: {
    setRowsNumber() {
      // get the rows count for the overview grid
      const rowsNumber = Math.ceil(
        document.getElementsByClassName('overview-info-item').length / 2,
      )
      document.documentElement.style.setProperty('--row-num', rowsNumber)
    },
    displayLocation() {
      const { country, city } = this.startup
      const location = city.length > 0 ? `${city}, ${country}` : country

      return location
    },
    formatFoundedDate() {
      const foundedAt = this.startup.founded_at
      if (foundedAt) {
        return dateFilter(foundedAt, 'MMM Y')
      }
      return '-'
    },
    isNumber: _.isNumber,
    formatSectors(trimValue = Infinity) {
      const { primary_sector: primary, secondary_sector: secondary } =
        this.startup

      if (primary && secondary) {
        return trim(
          `${sectorLabel(primary)}, ${sectorLabel(secondary)}`,
          trimValue,
        )
      }

      return sectorLabel(primary) || sectorLabel(secondary)
    },
  },
}
</script>

<style scoped lang="scss">
@import '~assets/css/colors.scss';

.grid-container {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

.overview-info-item {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  margin-bottom: 15px;

  span {
    margin: 10px 0 5px;
  }
}

@media (min-width: theme('screens.sm')) {
  .grid-container {
    display: grid;
    grid-template-rows: repeat(var(--row-num), 86px);
    grid-template-columns: 1fr 1fr;
    grid-auto-flow: column;
    column-gap: 10px;
  }

  .overview-info-item {
    display: grid;
    grid-template-columns: 1fr;
  }
}

@media (min-width: theme('screens.sm')) {
  .grid-container {
    grid-template-rows: repeat(var(--row-num), 43px);
  }

  .overview-info-item {
    grid-template-columns: 1fr 1fr;
  }
}

.dashboard-value a {
  color: $black;
}

.social-container {
  line-height: 1;
  margin-top: 2px;
}
</style>
